/**
 * What "the collection" IS.
 *
 * There is no table, so a scope is a query. The consequence is easy to get
 * wrong and expensive to debug: the value that drives `query()` and the value
 * that drives `deriveShape()` must be the *same* value, or the shape describes
 * a collection other than the one on screen and the generator lays out a
 * collection nobody is looking at.
 */

import type { TQuery } from '@morph/spec';
import type { ManifestRef, ObjectId } from './ids.js';
import type { Value } from './value.js';

export interface Scope {
  readonly manifest: ManifestRef;
  readonly query?: TQuery;                       // roles only; the spec's own query type
  readonly viewState?: readonly Value[];         // resolves Predicate.valueFrom indexes
  readonly seed?: readonly ObjectId[];           // single-object lenses, traversal roots
  readonly typeHint?: string;

  /**
   * Manifests this lens may follow a reference INTO — the cross-doc reference.
   *
   * A reference to an object in another pool projects to nothing today, and
   * for a reason worth keeping: `projectOne` walks the READING manifest's
   * roles, the target carries none of its keys, and the result is an empty
   * object. That is the manifest doing its job.
   *
   * So a cross-doc reference cannot be a discovery. The store holds every
   * manifest and could find one that fits the target — and that would silently
   * dissolve the boundary, because then every reference reads everything.
   * `across` is the opposite: the lens DECLARES which documents it reads from,
   * the way a Coda cross-doc sync is set up once and visible in the doc.
   *
   * Deliberately narrow. It widens `expand` and nothing else: a scope may read
   * what it POINTS AT, never what it can merely name. Seeding a foreign id
   * still resolves no roles, whatever is listed here —
   * `test/cross-doc.test.ts` asserts exactly that with `across` set, and
   * `test/manifest-boundary.test.ts` is why it has to.
   */
  readonly across?: readonly ManifestRef[];
}

/** Recursively order object keys before hashing, so two scopes that mean the
 *  same thing key the same digest. Lifted from `runtime/src/compile.ts` — the
 *  bug it was written for (a top-level replacer acting as a whitelist at every
 *  depth) is exactly as available here. */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, child]) => [key, canonicalize(child)]),
    );
  }
  return value;
}

/** FNV-1a over canonical JSON. A cache key, not a security boundary. */
export function stableHash(value: unknown): string {
  const json = JSON.stringify(canonicalize(value));
  let h = 2166136261;
  for (let i = 0; i < json.length; i += 1) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/** Stable hash; digest key and cache key. The seed list is *not* sorted — a
 *  seeded lens shows objects in the order it was given them, so two orders are
 *  two scopes. */
export function scopeKey(s: Scope): string {
  return stableHash({
    manifest: s.manifest,
    query: s.query ?? null,
    viewState: s.viewState ?? null,
    seed: s.seed ?? null,
    typeHint: s.typeHint ?? null,
    // Two lenses that differ only in what they may read across are two
    // lenses. Leaving this out would serve one of them from the other's
    // cached render.
    across: s.across ?? null,
  });
}

/**
 * A bounded read through one stable scope ordering.
 *
 * Cursors are for forward traversal. An absolute offset is for a viewport that
 * can jump in either direction after the runtime resolves a measured window.
 * They are deliberately exclusive: supplying both would leave the store to
 * guess which position owns the read.
 *
 * Absolute positions are valid for the current membership/order epoch only. A
 * host that observes a membership change must re-anchor by object identity
 * before issuing another offset page; an offset is never an object identity.
 */
type PagePosition =
  | { readonly cursor?: string; readonly offset?: never }
  | { readonly offset: number; readonly cursor?: never };

export type Page = { readonly limit: number } & PagePosition;

export interface Slice<T> {
  readonly items: readonly T[];
  readonly cursor?: string;
  readonly total?: number;
}
